// frontend/src/features/livestock/LivestockPickerRow.tsx
import { memo } from 'react';
import { Avatar, Badge, Box, Group, Text, UnstyledButton } from '@mantine/core';
import { STATUS_OPTIONS, type Livestock } from '@/types/livestock';
import type { PickerThumbSize } from './pickerThumbSize';
import classes from './LivestockPickerRow.module.css';

export interface LivestockPickerRowProps {
  livestock: Livestock;
  /** The chosen organism (aria-selected). */
  selected?: boolean;
  /** The result Enter would pick, while typing on a phone. */
  active?: boolean;
  /** Keep it stable (useCallback): rows are memoised. */
  onSelect: (livestock: Livestock) => void;
  /** Element id, for aria-activedescendant. */
  id?: string;
  /** Small and medium are rows; large is a tile for a grid (OrganismResults). */
  size?: PickerThumbSize;
}

/** The picture's side in a row; a tile's picture fills the tile's width. */
const ROW_PICTURE_SIZE: Record<Exclude<PickerThumbSize, 'large'>, number> = {
  small: 44,
  medium: 80,
};

const STATUS_COLOR = Object.fromEntries(
  STATUS_OPTIONS.map((option) => [option.value, option.color])
);

/** "Acropora sp. · SPS", or "SPS · Acropora" without a scientific name. */
function detailsOf(livestock: Livestock): string {
  const parts = livestock.scientific_name
    ? [livestock.scientific_name, livestock.category]
    : [livestock.category, livestock.subcategory];
  return parts.filter(Boolean).join(' · ');
}

/**
 * An organism to pick, as an option of a listbox: its picture (telling corals
 * apart is the point), the common name, and the scientific name · category.
 * Deceased and sold organisms carry their status. Out of the Tab order: the
 * search box moves through the options (aria-activedescendant).
 */
export const LivestockPickerRow = memo(function LivestockPickerRow({
  livestock,
  selected = false,
  active = false,
  onSelect,
  id,
  size = 'small',
}: LivestockPickerRowProps): JSX.Element {
  const details = detailsOf(livestock);
  const showStatus = livestock.status === 'deceased' || livestock.status === 'sold';
  const tile = size === 'large';
  const status = showStatus ? (
    <Badge
      size="xs"
      variant="light"
      color={STATUS_COLOR[livestock.status] ?? 'gray'}
      mt={tile ? 4 : undefined}
      style={{ flexShrink: 0 }}
    >
      {livestock.status}
    </Badge>
  ) : null;

  return (
    <UnstyledButton
      id={id}
      role="option"
      tabIndex={-1}
      aria-selected={selected}
      data-name={livestock.common_name}
      data-selected={selected || undefined}
      data-active={active || undefined}
      data-size={size}
      className={classes.row}
      onClick={() => onSelect(livestock)}
    >
      {tile ? (
        <>
          <Box aria-hidden>
            {/* Sized by the tile (CSS); 160 sets the letter's size when there's no picture */}
            <Avatar
              src={livestock.thumbnail_url}
              size={160}
              radius="sm"
              alt=""
              className={classes.tilePicture}
            >
              {livestock.common_name.charAt(0)}
            </Avatar>
          </Box>
          {/* Two lines, so variants ("Green Acro (purple tips)") stay told apart */}
          <Text size="sm" fw={500} lineClamp={2} lh={1.3} mt={6}>
            {livestock.common_name}
          </Text>
          {details && (
            <Text size="xs" c="gray.7" truncate lh={1.35}>
              {details}
            </Text>
          )}
          {status}
        </>
      ) : (
        <Group gap="sm" wrap="nowrap">
          <Box aria-hidden style={{ flexShrink: 0 }}>
            <Avatar src={livestock.thumbnail_url} size={ROW_PICTURE_SIZE[size]} radius="sm" alt="">
              {livestock.common_name.charAt(0)}
            </Avatar>
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate lh={1.35}>
              {livestock.common_name}
            </Text>
            {details && (
              <Text size="xs" c="gray.7" truncate lh={1.35}>
                {details}
              </Text>
            )}
          </Box>
          {status}
        </Group>
      )}
    </UnstyledButton>
  );
});
